// src/components/admin/adminUtils.js
// Admin 域共享工具：从 AdminDashboard.jsx 抽取，供多个 admin 组件复用。
import { useState } from "react";

const ENCODED_OCTET_RE = /%[0-9A-Fa-f]{2}/;

export const decodePossiblyEncodedText = (value) => {
  if (value == null) return '';
  let text = String(value);
  for (let i = 0; i < 3 && ENCODED_OCTET_RE.test(text); i += 1) {
    try {
      const next = decodeURIComponent(text);
      if (next === text) break;
      text = next;
    } catch {
      break;
    }
  }
  return text;
};

export const displayUserId = (value, fallback = '-') => {
  const text = decodePossiblyEncodedText(value).trim();
  // 系统占位 userId 统一显示为友好标签，避免后台出现 default_user/unknown/anonymous/未归属 等裸值
  const PLACEHOLDER = { default_user: '未归属(系统)', unknown: '未归属(系统)', anonymous: '未归属(系统)', 未归属: '未归属(系统)' };
  if (PLACEHOLDER[text]) return PLACEHOLDER[text];
  return text || fallback;
};

export const formatCostSpec = (row = {}) => {
  const parts = [];
  if (row.duration != null && row.duration !== '') parts.push(`${row.duration}s`);
  if (row.resolution) parts.push(row.resolution);
  if (!parts.length && row.mode) parts.push(row.mode);
  return parts.length ? parts.join(' · ') : '-';
};

export const shortText = (value, len = 80) => {
  if (value == null || value === '') return '-';
  const text = String(value);
  return text.length > len ? `${text.slice(0, len)}...` : text;
};

export const formatMoney = (value) => {
  const n = Number(value || 0);
  return `¥${Number.isFinite(n) ? n.toFixed(2) : '0.00'}`;
};

export const formatDetailedMoney = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '¥0.00';
  if (n === 0) return '¥0.00';
  return `¥${n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`;
};

export const formatTraceTime = (value) => {
  if (!value) return '-';
  try { return new Date(value).toLocaleString(); } catch { return '-'; }
};

export const userMatchesFilter = (value, filter) => {
  const needle = displayUserId(filter, '').trim().toLowerCase();
  if (!needle) return true;
  const raw = value == null ? '' : String(value).toLowerCase();
  const decoded = displayUserId(value, '').toLowerCase();
  return raw.includes(needle) || decoded.includes(needle);
};

export const rowMatchesAdminScope = (row, scope = {}) => {
  if (!row) return false;
  const scopedProjectId = scope.projectId == null ? '' : String(scope.projectId);
  if (scopedProjectId) {
    const rowProjectId = row.projectId ?? row.project_id;
    if (String(rowProjectId ?? '') !== scopedProjectId) return false;
  }
  if (!userMatchesFilter(row.userId ?? row.user_id, scope.userId)) return false;
  return true;
};

export const useAdminScope = (projectId) => {
  const [scope, setScope] = useState({ projectId: projectId || '', userId: '' });
  // projectId 变化时在渲染期同步（React 推荐的 render-adjust 模式，避免 effect 级联渲染）
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  if (prevProjectId !== projectId) {
    setPrevProjectId(projectId);
    setScope(prev => ({ ...prev, projectId: projectId || '' }));
  }
  return [scope, setScope];
};
